import React, { FC } from "react";
import { GetStaticProps } from "next";
import { cmsPageProps } from "../../lib/pageProps";
import { Trans, useTranslation } from "next-i18next";
import Breadcrumbs from "../../components/_shared/Breadcrumbs";
import BookableClasses from "../../components/skills-hub/BookableClasses";
import ClosureNotice from "../../components/skills-hub/ClosureNotice";
import NoClassesNotice from "../../components/skills-hub/NoClassesNotice";
import SolsEduCourses from "../../components/skills-hub/SolsEduCourses";
import ShareAMemory from "../../components/skills-hub/ShareAMemory";
import AppContainer from "../../layouts/AppContainer";
import Meta from "../../layouts/Meta";
import SkillsLibrary from "../../components/our-solution/SkillsLibrary";
import { Divider } from "@mui/material";
import { buildSkillsHubSchema } from "../../lib/skillsHubSchema";
import { BookableCategory, getBookableSkillsHub } from "../../lib/skillsHub";
import { SKILLS_HUB_OPEN } from "../../constants/skillsHub";

// Two states, set by SKILLS_HUB_OPEN in constants/skillsHub.js.
//
// Open: the bookable categories from lib/skillsHub.ts, through BookableClasses
// (one category's classes directly, or the category grid). With none, a "no
// classes open right now" notice and the SOLS edu card instead. The Skills Hub
// JSON-LD lists the same categories.
//
// Closed: the closure notice, the SOLS edu card and the invitation to share a
// memory. Fully static, with no Firebase read, and the closure copy comes from
// its own namespace so the open page never ships it.
//
// The Skills Library block shows in both. It describes the skill areas the
// Foundation works in rather than any bookable class, so it is true either way.
const Divide = ({ margin }: { margin: string }) => (
  <div style={{ margin }}>
    <Divider />
  </div>
);

const OpenSkillsHub: FC<{ bookable: BookableCategory[] }> = ({ bookable }) => {
  const { t } = useTranslation("skills-hub");
  const categories = bookable.map((entry) => entry.category);
  const schemaJson = bookable.length
    ? JSON.stringify(buildSkillsHubSchema(categories)).replace(/</g, "\\u003c")
    : null;

  return (
    <AppContainer>
      <Meta
        title={t("title")}
        description={t("meta-description")}
        keywords={t("meta-keyword")}
      />
      {schemaJson && (
        <script
          type="application/ld+json"
          // eslint-disable-next-line react/no-danger -- JSON-LD injection, same pattern as pages/index.tsx
          dangerouslySetInnerHTML={{ __html: schemaJson }}
        />
      )}
      <Breadcrumbs url="/skills-hub" />
      <h1 className="title">{t("title")}</h1>
      {bookable.length === 0 ? (
        <>
          <NoClassesNotice t={t} />
          <SolsEduCourses t={t} />
        </>
      ) : (
        <>
          <p className="description">
            <Trans i18nKey="intro-p" t={t} components={{ strong: <strong /> }} />
          </p>
          <BookableClasses t={t} bookable={bookable} />
        </>
      )}
      <Divide margin="65px auto 35px" />
      <SkillsLibrary t={t} />
    </AppContainer>
  );
};

const ClosedSkillsHub: FC = () => {
  const { t } = useTranslation("skills-hub");
  const { t: tClosed } = useTranslation("skills-hub-closed");

  return (
    <AppContainer>
      <Meta
        title={t("title")}
        description={tClosed("meta-description")}
        keywords={t("meta-keyword")}
      />
      <Breadcrumbs url="/skills-hub" />
      <h1 className="title">{t("title")}</h1>
      <ClosureNotice t={tClosed} />
      <SolsEduCourses t={t} />
      <Divide margin="65px auto 35px" />
      <SkillsLibrary t={t} />
      <Divide margin="55px auto 35px" />
      <ShareAMemory t={tClosed} />
    </AppContainer>
  );
};

const SkillsHub: FC<Props> = ({ bookable }) =>
  SKILLS_HUB_OPEN ? <OpenSkillsHub bookable={bookable} /> : <ClosedSkillsHub />;

interface Props {
  bookable: BookableCategory[];
}

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  if (!SKILLS_HUB_OPEN) {
    return {
      props: {
        bookable: [],
        ...(await cmsPageProps(locale, [
          "skills-hub",
          "skills-hub-closed",
        ])),
      },
      revalidate: 60,
    };
  }

  return {
    props: {
      bookable: await getBookableSkillsHub(),
      ...(await cmsPageProps(locale, ["skills-hub"])),
    },
    revalidate: 60,
  };
};

export default SkillsHub;
